// src/components/ratechart/AddEditRatechard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ratechart.css";
import StepDialog from "./StepDialog";

const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

function toTenths(v) {
  return Math.round(n(v) * 10);
}
function fromTenths(t) {
  return (t / 10).toFixed(1);
}

function normalizeSteps(arr) {
  const list = Array.isArray(arr) ? arr : [];
  const out = list
    .map((x) => ({
      step: Number(n(x?.step ?? x?.s ?? x?.from ?? x?.value).toFixed(1)),
      amount: Number(
        n(
          x?.amount ??
          x?.step_amount ??
          x?.amt ??
          x?.inc ??
          x?.rate ??
          x?.r
        ).toFixed(2)
      ),
    }))
    .filter((x) => Number.isFinite(x.step));

  out.sort((a, b) => a.step - b.step);
  const map = new Map();
  for (const it of out) map.set(it.step.toFixed(1), it);
  return Array.from(map.values()).sort((a, b) => a.step - b.step);
}

function cumulativeIncrease(targetVal, steps) {
  if (!steps || steps.length === 0) return 0;

  const sorted = normalizeSteps(steps);
  const start = sorted[0].step;
  const target = n(targetVal);
  if (target <= start) return 0;

  const tTarget = toTenths(target);

  let inc = 0;
  for (let i = 0; i < sorted.length; i++) {
    const segStart = toTenths(sorted[i].step);
    const segAmt = n(sorted[i].amount);
    const nextBoundary =
      i + 1 < sorted.length ? toTenths(sorted[i + 1].step) : tTarget;

    const segEnd = Math.min(nextBoundary, tTarget);

    if (segEnd > segStart) {
      const count = segEnd - segStart; // tenths count
      inc += count * segAmt;
    }

    if (tTarget <= nextBoundary) break;
  }

  return inc;
}

// ✅ Your dropdown options
const FORMAT_OPTIONS = [
  { key: "FAT_SNF", label: "FAT + SNF" },
  { key: "FAT_ONLY", label: "FAT only" },
  { key: "SNF_ONLY", label: "SNF only" },
];

const TYPE_OPTIONS = [
  { key: "INC_PER_POINTS", label: "Increase per fat/snf points" },
  { key: "DIRECT_RATE", label: "Direct rate table" },
  { key: "BASE_PLUS_FAT", label: "Base + FAT increase" },
  { key: "BASE_PLUS_SNF", label: "Base + SNF increase" },
];

export default function AddEditRatechard({
  open,
  milk = "cow",
  ratechartno = 1,
  chartData = null,
  onClose,
  onSave,
  mainuser = "",    
}) {
  const [name, setName] = useState("");
  const [startingAmount, setStartingAmount] = useState("0");
  const [fatSteps, setFatSteps] = useState([]);
  const [snfSteps, setSnfSteps] = useState([]);

  const [formatKey, setFormatKey] = useState("FAT_SNF");
  const [typeKey, setTypeKey] = useState("INC_PER_POINTS");

  const [saving, setSaving] = useState(false);

  const [stepDlg, setStepDlg] = useState({ open: false, kind: "fat" });
  const initialRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const pts = chartData?.points || chartData?.data?.points || null;

    const initName =
      chartData?.name ||
      (milk === "buf" ? "म्हैस Rate Chart" : "गाय Rate Chart");

    const initStart =
      pts?.initial ??   
      pts?.start_amount ??
      pts?.starting_amount ??
      chartData?.start_amount ??
      chartData?.starting_amount ??
      chartData?.preview?.startAmount ??
      0;

    const initFat = normalizeSteps(pts?.fat_steps || chartData?.fat_steps || []);
    const initSnf = normalizeSteps(pts?.snf_steps || chartData?.snf_steps || []);

    const fatDefault = initFat.length ? initFat : [{ step: 0.0, amount: 0.0 }];
    const snfDefault = initSnf.length ? initSnf : [{ step: 0.0, amount: 0.0 }];

    const initFormatRaw =
      chartData?.format_key || pts?.format_key || chartData?.format || pts?.format;
    const initTypeRaw =
      chartData?.type_key || pts?.type_key || chartData?.calc_type || pts?.calc_type;

    const initFormat = FORMAT_OPTIONS.some((x) => x.key === initFormatRaw)
      ? initFormatRaw
      : "FAT_SNF";
    const initType = TYPE_OPTIONS.some((x) => x.key === initTypeRaw)
      ? initTypeRaw
      : "INC_PER_POINTS";

    const init = {
      name: initName,
      startingAmount: String(n(initStart).toFixed(2)),
      fatSteps: fatDefault,
      snfSteps: snfDefault,
      formatKey: initFormat,
      typeKey: initType,
    };

    initialRef.current = init;

    setName(init.name);
    setStartingAmount(init.startingAmount);
    setFatSteps(init.fatSteps);
    setSnfSteps(init.snfSteps);
    setFormatKey(init.formatKey);
    setTypeKey(init.typeKey);
    setSaving(false);
  }, [open, chartData, milk]);

  const milkLabel = milk === "buf" ? "Buffalo" : "Cow";

  const useFat = formatKey !== "SNF_ONLY";
  const useSnf = formatKey !== "FAT_ONLY";

  const fatAxis = useMemo(() => {
    if (!useFat) return ["0.0"];
    const s = normalizeSteps(fatSteps);
    if (!s.length) return ["0.0"];
    const a = toTenths(s[0].step);
    const b = toTenths(s[s.length - 1].step);
    const out = [];
    for (let t = a; t <= b; t++) out.push(fromTenths(t));
    return out.length ? out : ["0.0"];
  }, [fatSteps, useFat]);

  const snfAxis = useMemo(() => {
    if (!useSnf) return ["0.0"];
    const s = normalizeSteps(snfSteps);
    if (!s.length) return ["0.0"];
    const a = toTenths(s[0].step);
    const b = toTenths(s[s.length - 1].step);
    const out = [];
    for (let t = a; t <= b; t++) out.push(fromTenths(t));
    return out.length ? out : ["0.0"];
  }, [snfSteps, useSnf]);

  const preview = useMemo(() => {
    const base = n(startingAmount);

    const grid = fatAxis.map((fat) => {
      const fi = useFat ? cumulativeIncrease(Number(fat), fatSteps) : 0;
      const row = snfAxis.map((snf) => {
        const si = useSnf ? cumulativeIncrease(Number(snf), snfSteps) : 0;
        return (base + fi + si).toFixed(2);
      });
      return row;
    });

    return { base, grid };
  }, [startingAmount, fatAxis, snfAxis, fatSteps, snfSteps, useFat, useSnf]);

  const isValid = useMemo(() => {
    const nm = String(name || "").trim();
    if (!nm) return false;

    // NOTE: allow 0
    const saRaw = String(startingAmount ?? "").trim();
    if (saRaw === "") return false;

    const f = normalizeSteps(fatSteps);
    const s = normalizeSteps(snfSteps);

    if (useFat && !f.length) return false;
    if (useSnf && !s.length) return false;

    return true;
  }, [name, startingAmount, fatSteps, snfSteps, useFat, useSnf]);

  const canSave = !saving && isValid;

  if (!open) return null;

  const addStep = (kind) => setStepDlg({ open: true, kind });

  const removeStep = (kind, stepValue) => {
    const key = String(stepValue);
    if (kind === "fat") {
      setFatSteps((prev) => prev.filter((x) => x.step.toFixed(1) !== key));
    } else {
      setSnfSteps((prev) => prev.filter((x) => x.step.toFixed(1) !== key));
    }
  };

  const handleApplyStep = (item, keepOpen) => {
    const listSetter = stepDlg.kind === "fat" ? setFatSteps : setSnfSteps;

    listSetter((prev) => {
      const cur = normalizeSteps(prev);
      const map = new Map(cur.map((x) => [x.step.toFixed(1), x]));
      map.set(item.step.toFixed(1), item);
      return Array.from(map.values()).sort((a, b) => a.step - b.step);
    });

    if (!keepOpen) setStepDlg({ open: false, kind: stepDlg.kind });
  };

  const reset = () => {
    const init = initialRef.current;
    if (!init) return;
    setName(init.name);
    setStartingAmount(init.startingAmount);
    setFatSteps(init.fatSteps);
    setSnfSteps(init.snfSteps);
    setFormatKey(init.formatKey);
    setTypeKey(init.typeKey);
  };

  // inside AddEditRatechard.jsx

const save = async () => {
  if (!canSave) return;

  if (typeof onSave !== "function") {
    alert("onSave is not passed from parent (Ratechart.jsx).");
    return;
  }

  const chartName = String(name || "").trim();
  if (!chartName) {
    alert("Name is required");
    return;
  }

  const type = milk === "buf" ? "buf" : "cow";
  const rcNo = Number(ratechartno) === 2 ? 2 : 1;

  // charttype stored in DB column (same as your PHP expects)
  const charttype =
    formatKey === "FAT_ONLY" ? "FAT"
    : formatKey === "SNF_ONLY" ? "SNF"
    : "FAT_SNF";

  // ✅ preview counts like Android (your screenshot: 31 & 6)
  // use the rendered axis lengths so server gets correct counts
  const fatCount = Array.isArray(fatAxis) ? fatAxis.length : 0;
  const snfCount = Array.isArray(snfAxis) ? snfAxis.length : 0;

  // ✅ Android stores initial as STRING (example: "85")
  const initialStr = String(n(startingAmount));

  // ✅ EXACT Android-style payload object
  const chartPayload = {
    mode: "POINTS",
    type,                         // ✅ ANDROID
    title: chartName,             // ✅ ANDROID

    points: {
      initial: initialStr,        // ✅ ANDROID (NOT start_amount)
      fat_steps: normalizeSteps(fatSteps).map((x) => ({
        step: x.step,
        amount: x.amount,
      })),
      snf_steps: normalizeSteps(snfSteps).map((x) => ({
        step: x.step,
        amount: x.amount,
      })),
    },

    preview: {
      fatCount,                   // ✅ ANDROID
      snfCount,                   // ✅ ANDROID
    },

    version: 1,
    mainuser: String(mainuser || "").trim(), // ✅ ANDROID
    device_ts: Date.now(),                   // ✅ ANDROID
    ratechartno: rcNo,                       // ✅ ANDROID
  };

  // ✅ Envelope root fields (PHP reads these from $in[...] )
  const envelope = {
    mainuser: String(mainuser || "").trim(),
    type,
    ratechartno: rcNo,
    category: "COLLECTION",
    name: chartName,
    charttype,
    payload: chartPayload,        // ✅ stored into DB payload column
  };

  console.log("save_ratechart envelope (ANDROID MATCH) ->", envelope);

  try {
    setSaving(true);
    await onSave(envelope);       // parent calls saveRatechartApi(envelope)
    onClose?.();
  } catch (e) {
    console.error("Save failed:", e);
    alert(e?.message || "Save failed");
  } finally {
    setSaving(false);
  }
};




  return (
    <div className="rcPage">
      <div className="rcEditTop">
        <button className="rcBackBtn" onClick={onClose} title="Back">
          ←
        </button>
        <div className="rcEditTopTitle">Edit Rate Chart</div>
      </div>

      <div className="rcEditContent">
        <div className="rcEditLeft">
          <div className="rcEditRow">
            <div className="rcEditLabel">Category :</div>
            <div className="rcEditValue">Collection</div>
          </div>

          <div className="rcEditRow">
            <div className="rcEditLabel">Milk Type:</div>
            <div className="rcEditValue">{milkLabel}</div>
          </div>

          <div className="rcEditRow">
            <div className="rcEditLabel">Name:</div>
            <input
              className="rcInput"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="rcEditRow">
            <div className="rcEditLabel">Format:</div>
            <select
              className="rcInput"
              value={formatKey}
              onChange={(e) => setFormatKey(e.target.value)}
            >
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="rcEditRow">
            <div className="rcEditLabel">Type:</div>
            <select
              className="rcInput"
              value={typeKey}
              onChange={(e) => setTypeKey(e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="rcEditRow">
            <div className="rcEditLabel">Starting amount:</div>
            <input
              className="rcInput"
              value={startingAmount}
              onChange={(e) => setStartingAmount(e.target.value)}
              inputMode="decimal"
            />
          </div>

          <div className="rcStepsWrap">
            <div className="rcStepsCol">
              <div className="rcStepsHead">
                <div>FAT steps</div>
                <button
                  className="rcLinkBtn"
                  onClick={() => addStep("fat")}
                  disabled={!useFat}
                  title={!useFat ? "Disabled for this Format" : ""}
                >
                  ＋ Add
                </button>
              </div>

              <table className="rcSmallTable">
                <tbody>
                  {normalizeSteps(fatSteps).map((x) => (
                    <tr key={x.step.toFixed(1)}>
                      <td>{x.step.toFixed(1)}</td>
                      <td>{n(x.amount).toFixed(2)}</td>
                      <td className="rcSmallCellEnd">
                        <button
                          className="rcIconBtn small"
                          onClick={() => removeStep("fat", x.step.toFixed(1))}
                          disabled={!useFat}
                          title={!useFat ? "Disabled for this Format" : "Remove"}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rcStepsCol">
              <div className="rcStepsHead">
                <div>SNF steps</div>
                <button
                  className="rcLinkBtn"
                  onClick={() => addStep("snf")}
                  disabled={!useSnf}
                  title={!useSnf ? "Disabled for this Format" : ""}
                >
                  ＋ Add
                </button>
              </div>

              <table className="rcSmallTable">
                <tbody>
                  {normalizeSteps(snfSteps).map((x) => (
                    <tr key={x.step.toFixed(1)}>
                      <td>{x.step.toFixed(1)}</td>
                      <td>{n(x.amount).toFixed(2)}</td>
                      <td className="rcSmallCellEnd">
                        <button
                          className="rcIconBtn small"
                          onClick={() => removeStep("snf", x.step.toFixed(1))}
                          disabled={!useSnf}
                          title={!useSnf ? "Disabled for this Format" : "Remove"}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rcEditActions">
            <button className="rcBtn" onClick={reset} disabled={saving}>
              Reset
            </button>

            <button
              className="rcBtn primary"
              onClick={save}
              disabled={!canSave}
              title={!isValid ? "Fill required fields" : ""}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="rcEditRight">
          <div className="rcPreviewWrap">
            <table className="rcTable rcGrid rcPreviewTable">
              <thead>
                <tr>
                  <th className="rcStickyLeft">fat_snf</th>
                  {snfAxis.map((snf) => (
                    <th key={snf}>{snf}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {fatAxis.map((fat, i) => (
                  <tr key={fat}>
                    <td className="rcStickyLeft">{fat}</td>
                    {snfAxis.map((snf, j) => (
                      <td key={snf}>{preview.grid?.[i]?.[j] ?? ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <StepDialog
          open={stepDlg.open}
          title={stepDlg.kind === "fat" ? "FAT steps" : "SNF steps"}
          onClose={() => setStepDlg({ open: false, kind: stepDlg.kind })}
          onApply={handleApplyStep}
        />
      </div>
    </div>
  );
}
